/**
 * Phase 1 export envelope — IV.A.1 (#74).
 *
 * The envelope captures everything required to reconstruct a Campaign
 * from its op log:
 *   - campaign metadata (id, name, owner, created_at, facilitation_mode,
 *     pending-auto-ratify policy)
 *   - the full op log (entity + fact + reveal ops) in HLC order
 *   - a convenience current-value snapshot (entities + fact heads) that
 *     a consumer can use without re-folding
 *   - asset references — Phase 1 carries metadata + signed URL only;
 *     blob bytes are NOT inlined (the spec freezes inline-blob support
 *     for later phases; resolution flagged in the PR body)
 *
 * Round-trip invariant: every op in `ops` is a complete `OpRecord` so a
 * test-harness importer can replay them in order against an empty
 * Campaign DO and obtain identical state. The fold from
 * `@dl/shared`/`foldOpsIntoEntities` reconstructs the snapshot bit-for-bit
 * (modulo timestamps inside the import, which the test harness preserves).
 *
 * The shape is **versioned at the top level** (`format_version: 1`) so
 * future phases can evolve the envelope without breaking importers — old
 * importers refuse the version they don't know, new importers carry a
 * back-compat path.
 *
 * Per CLAUDE.md "op log is the source of truth": export reads the op log
 * directly. The current-value snapshot is a convenience cache only — the
 * importer MUST replay ops, never trust the snapshot.
 */

import { HlcSchema } from '@dl/shared';
import { z } from 'zod';

import { OpRecordSchema } from './op-record.js';

/** Current export envelope version. */
export const EXPORT_FORMAT_VERSION = 1 as const;

/**
 * Campaign metadata as it appears in the export header. Mirrors the row
 * shape from D1's `campaigns` table at the time the export was taken.
 */
export const ExportCampaignMetadataSchema = z
  .object({
    id: z.string().min(1),
    name: z.string(),
    owner_account_id: z.string().min(1),
    created_at: z.iso.datetime({ offset: true }),
    facilitation_mode: z.enum(['gm', 'solo', 'rotating', 'flat']),
    pending_auto_ratify_policy: z
      .enum(['manual', 'auto_ratify', 'appear_as_pending'])
      .default('manual'),
  })
  .strict();
export type ExportCampaignMetadata = z.infer<typeof ExportCampaignMetadataSchema>;

/**
 * Asset reference. Phase 1 carries metadata + signed URL only — the bytes
 * stay on R2 (or equivalent). The PR body's scope question explicitly
 * confirms references-only for Phase 1.
 */
export const ExportAssetRefSchema = z
  .object({
    id: z.string().min(1),
    /** Original filename if known; renderers use this as a download hint. */
    filename: z.string().min(1).optional(),
    /** Content type if known; `application/octet-stream` is the safe default. */
    content_type: z.string().min(1).optional(),
    /** Signed URL valid at export time. Renderers should re-fetch on expiry. */
    signed_url: z.url().optional(),
    /** Byte length if known (Phase 1: optional; computed-on-write). */
    size_bytes: z.number().int().nonnegative().optional(),
  })
  .strict();
export type ExportAssetRef = z.infer<typeof ExportAssetRefSchema>;

/**
 * Current-value snapshot — derived from the op log at export time. The
 * importer is REQUIRED to ignore this and re-fold the ops; the field
 * exists so consumers (other tools, partial imports, audit reports) can
 * skip the fold if all they need is a snapshot.
 *
 * Shape: `{ entities: Record<entityId, EntityState> }` where `EntityState`
 * is the same shape produced by `@dl/shared/entities-fold.ts` —
 * intentionally loose at the protocol layer so we don't double-couple the
 * envelope to the fold's exact runtime types. Snapshot consumers should
 * treat it as informational.
 */
export const ExportSnapshotSchema = z
  .object({
    entities: z.record(z.string(), z.unknown()),
  })
  .strict();
export type ExportSnapshot = z.infer<typeof ExportSnapshotSchema>;

/**
 * The complete export envelope. Stable wire shape — additive evolution
 * MUST bump `format_version` if any field becomes load-bearing for
 * existing consumers.
 */
export const ExportEnvelopeV1Schema = z
  .object({
    format_version: z.literal(EXPORT_FORMAT_VERSION),
    /** Timestamp at which this export was generated (ISO 8601, with offset). */
    generated_at: z.iso.datetime({ offset: true }),
    /**
     * Generator identifier — useful when debugging an old export that
     * exposes a bug in a specific worker revision. Free-form string.
     */
    generator: z.string().min(1),
    /**
     * Viewer perspective the export was generated for. `gm` returns the
     * full op log (including drafts, hypotheticals, beliefs, reveals).
     * Non-GM is reserved for the Markdown export path which never emits a
     * JSON envelope today — the JSON export endpoint is GM-only by
     * construction (membership-checked + future-phase player JSON export
     * lands with the Phase 2 player content authorship surface).
     */
    perspective: z.enum(['gm']).default('gm'),
    campaign: ExportCampaignMetadataSchema,
    /**
     * The full op log in HLC order. The importer replays these against
     * an empty Campaign DO; the resulting predicate_head / current-value
     * snapshot MUST equal the original (round-trip invariant).
     */
    ops: z.array(OpRecordSchema(z.unknown())),
    /** Convenience snapshot — informational only. */
    snapshot: ExportSnapshotSchema,
    /** Asset references (no blobs in Phase 1 — see scope note above). */
    assets: z.array(ExportAssetRefSchema).default([]),
    /**
     * Server-reported tip HLC at export time. The importer surfaces this
     * to the user so they can confirm they got the version they expected.
     */
    server_hlc: HlcSchema.nullable(),
  })
  .strict();
export type ExportEnvelopeV1 = z.infer<typeof ExportEnvelopeV1Schema>;

/**
 * Default generator identifier for the Phase 1 worker export path.
 * Bump the version segment when the export shape changes — even an
 * additive change deserves a generator bump so we can correlate
 * downstream bugs.
 */
export const DEFAULT_EXPORT_GENERATOR = 'dl-worker-export@1';
